import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useRenderDebug } from '@/hooks/useRenderDebug';
import { trackEvent } from '@/lib/posthog';
import type { AccountNumberingScheme, SetStarredResponse } from '@releve/shared';
import { authedPut } from '@/lib/api';
import { captureException } from '@/lib/sentry';

interface Organization {
  id: string;
  name: string;
  description: string | null;
  currency: string;
  country: string | null;
  nace_code: string | null;
  employee_count: number | null;
  annual_revenue: number | null;
  timezone: string | null;
  default_reporting_period: string | null;
  default_landing_page: string | null;
  theme_preset: 'default' | 'purple' | 'calm' | 'blumenthal' | 'releve';
  created_at: string;
  // Image fields
  logo_small: string | null;
  logo_medium: string | null;
  logo_large: string | null;
  // Chart-of-accounts settings (Settings → Accounting → Chart of accounts)
  account_numbering_scheme: AccountNumberingScheme;
  show_account_numbers: boolean;
  // Membership-specific fields
  member_id: string;
  is_starred: boolean;
}

interface OrganizationContextType {
  selectedOrganization: Organization | null;
  organizations: Organization[];
  setSelectedOrganization: (org: Organization) => void;
  setSelectedOrganizationById: (orgId: string) => void;
  toggleStarred: (org: Organization) => Promise<void>;
  loading: boolean;
  refreshOrganizations: () => Promise<void>;
  recentOrgIds: string[];
  addToRecent: (orgId: string) => void;
}

const RECENT_ORGS_KEY = 'sustaina-recent-orgs';
const MAX_RECENT_ORGS = 3;

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [selectedOrganization, setSelectedOrganizationState] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [recentOrgIds, setRecentOrgIds] = useState<string[]>(() => {
    // Initialize from localStorage
    try {
      const stored = localStorage.getItem(RECENT_ORGS_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  // Track the user ID we last fetched for to avoid race conditions
  const lastFetchedUserIdRef = useRef<string | null>(null);
  // Ref to read selectedOrganization without making callbacks depend on it
  const selectedOrgRef = useRef(selectedOrganization);
  selectedOrgRef.current = selectedOrganization;
  // Ref to read user/authLoading without making fetchOrganizations depend on them
  const userRef = useRef(user);
  userRef.current = user;
  const authLoadingRef = useRef(authLoading);
  authLoadingRef.current = authLoading;

  // Add organization to recent list
  const addToRecent = useCallback((orgId: string) => {
    setRecentOrgIds(prev => {
      // Remove if already exists, then add to front
      const filtered = prev.filter(id => id !== orgId);
      const updated = [orgId, ...filtered].slice(0, MAX_RECENT_ORGS);
      // Save to localStorage
      try {
        localStorage.setItem(RECENT_ORGS_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error('Failed to save recent orgs to localStorage:', e);
      }
      return updated;
    });
  }, []);

  const fetchOrganizations = useCallback(async () => {
    const currentUser = userRef.current;
    const isAuthLoading = authLoadingRef.current;

    // If auth is still loading, don't fetch yet - wait for auth to complete
    if (isAuthLoading) {
      return;
    }

    if (!currentUser) {
      setOrganizations([]);
      setSelectedOrganizationState(null);
      setLoading(false);
      lastFetchedUserIdRef.current = null;
      return;
    }

    // Skip if we've already fetched for this user
    if (lastFetchedUserIdRef.current === currentUser.id) {
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('organization_members')
        .select(`
          id,
          organization_id,
          is_starred,
          organizations (
            id,
            name,
            description,
            currency,
            country,
            nace_code,
            employee_count,
            annual_revenue,
            timezone,
            default_reporting_period,
            default_landing_page,
            theme_preset,
            created_at,
            logo_small,
            logo_medium,
            logo_large,
            account_numbering_scheme,
            show_account_numbers
          )
        `)
        .eq('user_id', currentUser.id);

      if (error) throw error;

      const orgs = data
        ?.map(item => ({
          ...item.organizations,
          member_id: item.id,
          is_starred: item.is_starred ?? false,
        }))
        .filter(Boolean) as Organization[];

      // Sort: starred orgs first, then alphabetically
      orgs.sort((a, b) => {
        if (a.is_starred && !b.is_starred) return -1;
        if (!a.is_starred && b.is_starred) return 1;
        return a.name.localeCompare(b.name);
      });

      setOrganizations(orgs || []);

      // Update selected organization with fresh data if it exists
      const currentSelectedOrg = selectedOrgRef.current;
      if (currentSelectedOrg && orgs?.length > 0) {
        const updatedOrg = orgs.find(org => org.id === currentSelectedOrg.id);
        if (updatedOrg) {
          setSelectedOrganizationState(updatedOrg);
        }
      }
      // Note: We don't auto-select anymore - that's handled by URL routing
      // Mark that we've successfully fetched for this user
      lastFetchedUserIdRef.current = currentUser.id;
    } catch (error) {
      console.error('Error fetching organizations:', error);
      setOrganizations([]);
      setSelectedOrganizationState(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshOrganizations = useCallback(async () => {
    // Reset the ref to allow re-fetching
    lastFetchedUserIdRef.current = null;
    await fetchOrganizations();
  }, [fetchOrganizations]);

  const toggleStarred = useCallback(async (org: Organization) => {
    const newStarredValue = !org.is_starred;

    // Optimistically update the UI
    setOrganizations(prev => {
      const updated = prev.map(o =>
        o.id === org.id ? { ...o, is_starred: newStarredValue } : o
      );
      // Re-sort after update
      updated.sort((a, b) => {
        if (a.is_starred && !b.is_starred) return -1;
        if (!a.is_starred && b.is_starred) return 1;
        return a.name.localeCompare(b.name);
      });
      return updated;
    });

    // Update selected org if it's the one being toggled
    const currentSelectedOrg = selectedOrgRef.current;
    if (currentSelectedOrg?.id === org.id) {
      setSelectedOrganizationState({ ...currentSelectedOrg, is_starred: newStarredValue });
    }

    // Save through the API (the caller's own membership row; any role may star).
    try {
      await authedPut<SetStarredResponse>(
        `/api/v1/organizations/${encodeURIComponent(org.id)}/membership/starred`,
        { starred: newStarredValue },
      );
    } catch (error) {
      captureException(error, { context: 'OrganizationContext.toggleStarred', organizationId: org.id });
      // Revert to the stored state (`refresh`, not `fetch`: the fetch skips a
      // user it has already loaded, so it would keep the optimistic flip).
      await refreshOrganizations();
    }
  }, [refreshOrganizations]);

  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations, user, authLoading]);

  // Reset when user changes
  useEffect(() => {
    if (!user) {
      setSelectedOrganizationState(null);
      setOrganizations([]);
    }
  }, [user]);

  // Set selected organization by navigating to the new org's dashboard
  // Only the explicit switcher calls this — the URL-driven selection below
  // (first load, deep links, OrgSelect's single-org auto-redirect) does not
  // count as a switch.
  const setSelectedOrganization = useCallback((org: Organization) => {
    if (org.id !== selectedOrgRef.current?.id) trackEvent('org_switched', {});
    navigate(`/org/${org.id}/dashboard`);
  }, [navigate]);

  // Ref to read organizations without making callbacks depend on it
  const organizationsRef = useRef(organizations);
  organizationsRef.current = organizations;

  // Set selected organization by ID (used by OrgLayout to sync context with URL)
  const setSelectedOrganizationById = useCallback((orgId: string) => {
    const org = organizationsRef.current.find(o => o.id === orgId);
    if (org) {
      setSelectedOrganizationState(org);
    }
  }, []);

  // DEBUG: Track what's causing provider re-renders
  useRenderDebug('OrganizationProvider', {
    selectedOrganization,
    organizations,
    setSelectedOrganization,
    setSelectedOrganizationById,
    toggleStarred,
    loading,
    refreshOrganizations,
    recentOrgIds,
    addToRecent,
    // also track upstream deps
    user,
    authLoading,
    navigate,
  });

  const value = useMemo(() => ({
    selectedOrganization,
    organizations,
    setSelectedOrganization,
    setSelectedOrganizationById,
    toggleStarred,
    loading,
    refreshOrganizations,
    recentOrgIds,
    addToRecent,
  }), [
    selectedOrganization,
    organizations,
    setSelectedOrganization,
    setSelectedOrganizationById,
    toggleStarred,
    loading,
    refreshOrganizations,
    recentOrgIds,
    addToRecent,
  ]);

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}